/**
 * Base Component class for Neemans theme - NMS (Neemans Modular System)
 * Provides refs, events, and window.neemans integration for all NMS components
 * Follows NMS JavaScript standards and best practices
 */

class NMSBaseClass {
  #eventController = null;

  constructor() {
    this.refs = {};
    this.cache = new Map();

    // Initialize global namespace safely
    window.neemans = window.neemans || {};
  }

  // Call this method after component is added to DOM
  init() {
    const container = this.element;

    if (!container?.querySelectorAll) return;

    this.refs = {};
    this.#setupRefs(container);
    this.#bindEvents(container);
  }

  // Auto-find elements with ref attributes in a container
  #setupRefs(container) {
    const refElements = [
      ...(container.matches?.('[ref]') ? [container] : []),
      ...container.querySelectorAll('[ref]'),
    ];

    for (const element of refElements) {
      const refName = element.getAttribute('ref');

      if (refName.endsWith('[]')) {
        // Array reference (e.g., ref="buttons[]")
        const arrayName = refName.slice(0, -2);
        if (!this.refs[arrayName]) this.refs[arrayName] = [];
        this.refs[arrayName].push(element);
      } else {
        // Single reference (e.g., ref="button")
        this.refs[refName] = element;
      }
    }
  }

  // Auto-bind events from data-nms-click attributes in a container
  #bindEvents(container) {
    this.#eventController?.abort();
    this.#eventController = new AbortController();

    const clickElements = [
      ...(container.matches?.('[data-nms-click]') ? [container] : []),
      ...container.querySelectorAll('[data-nms-click]'),
    ];

    for (const element of clickElements) {
      const handlerPath = element.getAttribute('data-nms-click');
      const [methodName, ...args] = handlerPath.split('/').filter(Boolean);

      if (this[methodName] && typeof this[methodName] === 'function') {
        element.addEventListener(
          'click',
          event => {
            this[methodName].call(this, event, ...args);
          },
          { signal: this.#eventController.signal }
        );
      }
    }
  }

  // Get data from window.neemans namespace
  getGlobalData(key) {
    const keys = key.split('.');
    let data = window.neemans;

    for (const k of keys) {
      data = data?.[k];
      if (data === undefined) return null;
    }

    return data;
  }

  // Set data in window.neemans namespace
  setGlobalData(key, value) {
    const keys = key.split('.');
    const lastKey = keys.pop();
    let data = window.neemans;

    for (const k of keys) {
      data[k] = data[k] || {};
      data = data[k];
    }

    data[lastKey] = value;
  }

  // Access existing theme utilities safely
  useThemeUtility(utilityName) {
    return window.theme?.[utilityName] || null;
  }

  // Debounce utility with theme fallback
  debounce(func, wait) {
    const themeDebounce = this.useThemeUtility('debounce');

    if (themeDebounce) {
      return themeDebounce(func, wait);
    }

    // Fallback implementation
    let timeout;
    return (...args) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => func.apply(this, args), wait);
    };
  }

  // Fetch data with error handling and caching
  async fetchData(url, options = {}) {
    const cacheKey = `fetch-${url}`;

    // Check cache first if enabled
    if (options.cache && this.cache.has(cacheKey)) {
      return this.cache.get(cacheKey);
    }

    try {
      const response = await fetch(url, {
        headers: {
          'Content-Type': 'application/json',
          ...options.headers,
        },
        ...options,
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();

      // Cache successful responses if enabled
      if (options.cache) {
        this.cache.set(cacheKey, data);
      }

      return data;
    } catch (error) {
      console.error(`Failed to fetch ${url}:`, error);

      // Return cached data as fallback
      if (this.cache.has(cacheKey)) {
        console.warn(`Using cached data for ${url}`);
        return this.cache.get(cacheKey);
      }

      throw error;
    }
  }

  // Dispatch NMS custom events on a target element
  dispatchNMSEvent(eventName, detail = {}, target = document) {
    const event = new CustomEvent(`nms:${eventName}`, {
      detail: {
        ...detail,
        component: this,
        timestamp: Date.now(),
      },
      bubbles: true,
      cancelable: true,
    });

    return target.dispatchEvent(event);
  }

  // Show loading state on element
  showLoading(element) {
    if (!element) return;

    element.classList.add('nms-loading');
    element.setAttribute('aria-busy', 'true');

    const originalText = element.textContent;
    element.dataset.originalText = originalText;

    if (element.tagName === 'BUTTON') {
      element.disabled = true;
      element.textContent = 'Loading...';
    }
  }

  // Hide loading state on element
  hideLoading(element) {
    if (!element) return;

    element.classList.remove('nms-loading');
    element.setAttribute('aria-busy', 'false');

    if (element.tagName === 'BUTTON') {
      element.disabled = false;
      element.textContent = element.dataset.originalText || element.textContent;
      delete element.dataset.originalText;
    }
  }

  // Format price using Shopify's money format
  formatPrice(cents, withCurrency = false) {
    // Use Shopify's configured money formats
    const format = withCurrency
      ? window.themeVariables?.settings?.moneyWithCurrencyFormat
      : window.themeVariables?.settings?.moneyFormat;

    if (format) {
      // Format amount with proper comma separators (Indian format: 3,499.00)
      const amount = (cents / 100).toLocaleString('en-IN', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });

      // Format amount without decimals for no_decimals placeholder
      const amountNoDecimals = Math.round(cents / 100).toLocaleString('en-IN');

      // Replace Shopify placeholders with formatted amounts
      return format
        .replace(/\{\{\s*amount\s*\}\}/g, amount)
        .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, amountNoDecimals);
    }

    // Should not reach here if Shopify data is properly loaded
    return (cents / 100).toFixed(2);
  }

  // Handle errors with user-friendly messages
  handleError(error, fallbackMessage = 'Something went wrong. Please try again.') {
    console.error('NMS Component Error:', error);

    // Dispatch error event for global error handling
    this.dispatchNMSEvent('error', {
      error: error.message,
      stack: error.stack,
      fallbackMessage,
    });

    return fallbackMessage;
  }

  // Standardized Overlay Backdrop Utilities

  /**
   * Show the global overlay backdrop
   * @param {Object} options - Overlay configuration
   * @param {number} [options.zIndex] - Z-index for overlay
   * @param {boolean} [options.closeOnBackdrop=true] - Whether to close on backdrop click
   * @param {boolean} [options.closeOnEscape=true] - Whether to close on escape key
   * @param {Function} [options.onClose] - Callback when overlay closes
   */
  showOverlayBackdrop(options = {}) {
    const overlay = document.querySelector('[data-ref="nmsOverlay"]');
    if (!overlay) {
      console.error('[NMS] Global overlay element not found');
      return false;
    }

    // Store callback for when overlay closes
    if (options.onClose && typeof options.onClose === 'function') {
      this.overlayCloseCallback = options.onClose;
    }

    // Set global flag to prevent cart drawer from closing
    window.neemans = window.neemans || {};
    window.neemans.overlayOpen = true;

    // Configure overlay
    overlay.setAttribute('aria-hidden', 'false');
    overlay.classList.add('nms-overlay--show');

    // Add z-index if specified
    if (options.zIndex) {
      overlay.style.zIndex = options.zIndex;
    }

    // Add click handler to close on backdrop click (if enabled)
    if (options.closeOnBackdrop !== false) {
      overlay.addEventListener('click', this.#handleBackdropClick.bind(this), { once: true });
    }

    // Add escape key handler (if enabled) - store bound reference for removal
    if (options.closeOnEscape !== false) {
      // Remove any previously stored handler first to prevent accumulation
      if (this._boundOverlayEscapeHandler) {
        document.removeEventListener('keydown', this._boundOverlayEscapeHandler);
      }
      this._boundOverlayEscapeHandler = this.#handleOverlayEscape.bind(this);
      document.addEventListener('keydown', this._boundOverlayEscapeHandler);
    }

    return true;
  }

  /**
   * Hide the global overlay backdrop
   */
  hideOverlayBackdrop() {
    const overlay = document.querySelector('[data-ref="nmsOverlay"]');
    if (!overlay) {
      return false;
    }

    // Clear global flag
    if (window.neemans) {
      window.neemans.overlayOpen = false;
    }

    // Hide overlay
    overlay.setAttribute('aria-hidden', 'true');
    overlay.classList.remove('nms-overlay--show');
    overlay.style.zIndex = '';

    // Remove event listeners using stored bound reference
    if (this._boundOverlayEscapeHandler) {
      document.removeEventListener('keydown', this._boundOverlayEscapeHandler);
      this._boundOverlayEscapeHandler = null;
    }

    return true;
  }

  /**
   * Handle backdrop click to close overlay
   * @param {MouseEvent} event
   * @private
   */
  #handleBackdropClick(event) {
    // Only close if clicking directly on the backdrop (not child elements)
    if (event.target.matches('[data-ref="nmsOverlay"]')) {
      this.hideOverlayBackdrop();

      // Call component callback if provided
      if (this.overlayCloseCallback) {
        this.overlayCloseCallback();
        this.overlayCloseCallback = null; // Clear callback after use
      }
    }
  }

  /**
   * Handle escape key to close overlay
   * @param {KeyboardEvent} event
   * @private
   */
  #handleOverlayEscape(event) {
    if (event.key === 'Escape') {
      this.hideOverlayBackdrop();

      // Call component callback if provided
      if (this.overlayCloseCallback) {
        this.overlayCloseCallback();
        this.overlayCloseCallback = null; // Clear callback after use
      }
    }
  }

  /**
   * Check if overlay backdrop is currently visible
   * @returns {boolean}
   */
  isOverlayBackdropVisible() {
    const overlay = document.querySelector('[data-ref="nmsOverlay"]');
    return overlay && overlay.classList.contains('nms-overlay--show');
  }

  // Cleanup resources - override in components that use overlay
  destroy() {
    // Hide overlay if this component was using it
    if (this.isOverlayBackdropVisible()) {
      this.hideOverlayBackdrop();
    }

    this.#eventController?.abort();
    this.#eventController = null;
    this.cache.clear();
  }
}

// Make available globally for all NMS components
window.NMSBaseClass = NMSBaseClass;

// Export for module systems (if needed)
if (typeof module !== 'undefined' && module.exports) {
  module.exports = NMSBaseClass;
}
